import { inject, Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CoreRepository, PaginatedResponse } from '@tcc-mono/shared/data-access';
import { Animal, Hub, TransportGroup, TransportGroupRemainingAnimals, TransportGroupRvoReportResult } from '@tcc-mono/vandijk/shared/data-access/models';
import { HubsRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { TransportGroupAnimalService, TransportGroupService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import dayjs from 'dayjs';
import { BehaviorSubject, Observable, map, switchMap, take, tap } from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class TransportGroupsRepository extends CoreRepository<TransportGroup> {
  private readonly _transportGroupAnimalService: TransportGroupAnimalService = inject(TransportGroupAnimalService);
  private readonly _hubRepo: HubsRepository = inject(HubsRepository);

  public transportGroups$: Observable<TransportGroup[]> = this.entities$;
  public selectedTransportGroup$: Observable<TransportGroup> = this.activeEntity$;

  private _selectedTransportGroupAnimals = new BehaviorSubject<Animal[]>([]);
  public selectedTransportGroupAnimals$: Observable<Animal[]> = this._selectedTransportGroupAnimals.asObservable()
    .pipe(
      map((animals) => animals?.sort((a, b) => {
        if (dayjs(a.created_at).isBefore(b.created_at)) {
          return 1;
        }
        if (dayjs(b.created_at).isBefore(a.created_at)) {
          return -1;
        }
        return 0;
      }))
    );

  private _animalLoading = new BehaviorSubject<boolean>(false);
  public animalLoading$ = this._animalLoading.asObservable();

  public constructor(
    protected readonly _transportGroupService: TransportGroupService
  ) {
    super(
      { name: 'transportGroups' },
      _transportGroupService
    );

    this._hubRepo.activeHub$
      .pipe(
        filterNil(),
        map((hub: Hub) => this.updateParams({ hub_id: hub.id })),
        untilDestroyed(this)
      )
      .subscribe();
  }

  public addTransportGroup = (transportGroup: Partial<TransportGroup>): Observable<TransportGroup> => {
    return this.addEntity(transportGroup);
  }

  public updateTransportGroup = (transportGroupId: number, transportGroup: TransportGroup): Observable<TransportGroup> => {
    return this.updateEntity(transportGroupId, transportGroup);
  }

  public getTransportGroup = (transportGroupId: number, setActive: boolean = false, forceGet: boolean = false): Observable<TransportGroup> => {
    return this.getEntity(transportGroupId, setActive, forceGet);
  }

  public removeTransportGroup = (transportGroupId: number): Observable<unknown> => {
    return this.removeEntity(transportGroupId);
  }

  public setActiveTransportGroup = (transportGroupId?: number): void => {
    this.setActiveEntity(transportGroupId);
  }

  public createRemainingAnimalGroup = (body: TransportGroupRemainingAnimals): Observable<unknown> => {
    return this._transportGroupService.postChild(body, 'create_from_hub')
      .pipe(
        tap((group: TransportGroup) => this.addEntitiesInStore([group]))
      );
  }

  public close = (groupId: number) => {
    return this._transportGroupService.close(groupId)
      .pipe(
        switchMap(() => this.getTransportGroup(groupId, true, true))
      );
  };

  public brightExport = (groupId: number) => {
    return this._transportGroupService.brightExport(groupId)
      .pipe(
        switchMap(() => this.getTransportGroup(groupId, true, true))
      );
  };

  public brightExportBulk = (transportGroupIds: number[]) => {
    return this._transportGroupService.brightExportBulk(transportGroupIds);
  };

  public downloadTransportPdf = (transportId: number) => {
    return this._transportGroupService.downloadTransportPdf(transportId);
  };

  public brightDownload = (transportGroupId: number) => {
    return this._transportGroupService
      .brightDownload(transportGroupId)
      .pipe(switchMap(() => this.getEntity(transportGroupId, true, true)));
  };

  public denkavitExport = (transportGroupIds: number[]) => {
    return this._transportGroupService.denkavitExport(transportGroupIds);
  };

  public brightExportBuyer = (transportGroupIds: number[]) => {
    return this._transportGroupService.brightExportBuyer(transportGroupIds);
  };

  public animalDetailsExport = (transportGroupIds: number[]) => {
    return this._transportGroupService.animalDetailsExport(transportGroupIds);
  };

  public brightExportBuyerMail = (transportGroupIds: number[]) => {
    return this._transportGroupService.brightExportBuyerMail(transportGroupIds);
  };

  public brightExportBuyerApi = (transportGroupIds: number[]) => {
    return this._transportGroupService.brightExportBuyerApi(transportGroupIds);
  };

  public denkavitExportMail = (transportGroupIds: number[]) => {
    return this._transportGroupService.denkavitExportMail(transportGroupIds);
  };

  public reportRvo = (groupId: number, test: boolean = true): Observable<TransportGroupRvoReportResult[]> => {
    return this._transportGroupService.reportRvo(groupId, test)
      .pipe(
        map(({ data }) => data),
        switchMap((results: TransportGroupRvoReportResult[]) => this.getTransportGroup(groupId, true, true).pipe(map(() => results)))
      );
  };

  public addAnimal(animal: Animal, transportGroupId: number, override: boolean = false): Observable<Animal> {
    const id = {
      animal_id: animal.id
    };
    return this._transportGroupAnimalService.postChild(
      { ...id },
      `${transportGroupId}/animals`,
      override ? { override } : null
    ).pipe(
      tap(() => this.getAnimals(transportGroupId))
    );
  }

  public deleteAnimal(transportGroupId: number, animalId: number): Observable<Animal> {
    return this._transportGroupService.deleteAnimal(transportGroupId, animalId).pipe(tap(() => this.getAnimals(transportGroupId)))
  }

  public transferAnimal(transportGroupId: number, animalId: number, override: boolean = false): Observable<Animal> {
    const id = {
      animal_id: animalId
    };
    return this._transportGroupAnimalService.postChild(
      { ...id },
      `${transportGroupId}/transfer_animal`,
      override ? { override } : null
    ).pipe(tap(() => this.getAnimals(transportGroupId)));
  }

  public getAnimals(transportGroupId: number) {

    this._transportGroupAnimalService.getAnimals(transportGroupId)
      .pipe(
        take(1),
        tap((animals: any) => this._selectedTransportGroupAnimals.next(animals.data))
      )
      .subscribe();
  }
}
